import { computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { AuthenticationContext } from "@/auth/msal-context";

const UNGROUPED = "";

// Strips optional route params such as "/event-view/:id?" so the link opens the base page.
function linkTarget(path) {
  return path.replace(/\/:[^/]+\?/g, "") || "/";
}

export function isAllowed(record) {
  const roles = record.meta?.requireRoles;
  return roles === undefined || AuthenticationContext.checkRoles(roles);
}

/**
 * Navigation entries derived from the router, filtered by the user's roles.
 * Used by the desktop sidebar and the mobile "Mehr" menu so both stay in sync.
 */
export function useNavigation() {
  const router = useRouter();
  const route = useRoute();

  const items = computed(() =>
    router.options.routes
      .filter((record) => !record.redirect && isAllowed(record))
      .map((record) => ({
        name: record.name,
        title: record.meta?.title ?? record.name,
        icon: record.meta?.icon,
        group: record.meta?.group ?? UNGROUPED,
        to: linkTarget(record.path),
      }))
  );

  const groups = computed(() => {
    const byGroup = new Map();
    items.value.forEach((item) => {
      if (!byGroup.has(item.group)) {
        byGroup.set(item.group, []);
      }
      byGroup.get(item.group).push(item);
    });
    return Array.from(byGroup, ([title, entries]) => ({ title, entries }));
  });

  const findByName = (name) => items.value.find((item) => item.name === name) ?? null;

  // Matches on the top-level route, so "/event-view/42/participants" keeps "Ausfahrten" highlighted.
  const isActive = (item) => route.matched[0]?.name === item.name;

  return { items, groups, findByName, isActive };
}
